import { ComponentRef, DestroyRef, Directive, ElementRef, HostBinding, HostListener, Input, OnInit, ViewContainerRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AdminUser } from '@Core/models/codegen/admin.models';
import { Area, TenantUser } from '@Core/models/codegen/configuration.models';
import { TenantContext } from '@Core/contexts/tenant-context';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '@Services/auth.service';
import { AllFeatureFacetsVM, FeatureFacetService } from '@Services/feature-facet.service';
import { UserService } from '@Services/user.service';
import { LoggingService } from '@Core/services/logging.service';
import { SmAssignableBadgeVM } from '@Shared/Components/sm-assignable-badge/sm-assignable-badge.viewmodel';
import _ from 'lodash';
import { combineLatest } from 'rxjs';
import { SelectAreaModalComponent } from '../../Views/Mktg/select-area/select-area-modal.component';
import { SmSimpleDialog, SimpleDialogOptions } from '../Components/sm-simple-dialog/sm-simple-dialog.component';
import { SmUserInfoComponent } from '../Components/sm-user-info/sm-user-info.component';
import { SmNotificationSettingsComponent } from '../Components/sm-notification-settings/sm-notification-settings.component';
import { TenantPermissions } from '@Core/enums/codegen/tenant-permissions.enum';
import { SyncHubStatusService } from '@Core/services/sync-hub-status.service';
import { SyncStatus } from '@Core/enums/sync-status.enum';

@Directive({
    selector: 'aurora-header[auroraAppHeader]',
    standalone: false
})
export class AuroraHeaderDirective implements OnInit {
    private static readonly SyncStatusStyleElementId = 'sm-sync-status-style';
    private static readonly TenantSwitchStyleElementId = 'sm-tenant-switch-style';

    // Host binding getters are re-read on every change detection pass and diffed with
    // Object.is. Returning a fresh array literal would make the binding look changed every
    // cycle, which writes the property onto the Lit element, which schedules a Lit update on
    // a zone-patched microtask, which stabilises the zone and triggers another pass - an
    // infinite loop that freezes the tab. Always hand back the same empty array instance.
    private static readonly NoAreas: Area[] = [];
    private static readonly NoInfo: { label: string; value: string }[] = [];

    @Input() view: string;

    private destroyRef = inject(DestroyRef);

    public user: TenantUser;
    public adminUser: AdminUser;
    public badge: SmAssignableBadgeVM;
    public areas: Area[] = [];
    public filteredAreas: Area[] = [];
    public allFacets: AllFeatureFacetsVM = null;

    public auroraUser: {
        initials: string;
        name: string;
        subline1: string;
        subline2: string;
        image: string;
        disabled: boolean;
    } = {
        initials: 'U',
        name: 'User',
        subline1: '',
        subline2: '',
        image: '',
        disabled: false
    };

    public auroraUserLinks: string[] = [];
    public auroraInfo: { label: string; value: string }[] = [];
    private syncStatus: SyncStatus = SyncStatus.Connected;

    @HostBinding('product')
    public get hostProduct(): string {
        if (this.view === 'Admin') {
            return 'Pro Suite';
        }

        const hasAdvancedWorkflows = FeatureFacetService.checkIfFacetExists(
            TenantPermissions.AdvancedWorkflows,
            this.allFacets,
            this.authService.spaName,
            this.authService.getAreaKey()
        );

        return hasAdvancedWorkflows ? 'Insurity Underwriting' : 'Sure Commercial';
    }

    @HostBinding('tenant')
    public get hostTenant(): string {
        if (this.authService.spaName === 'admin') {
            return 'Admin';
        }

        return this.authService.getTenantDisplayName();
    }

    @HostBinding('area')
    public get hostArea(): string {
        if (this.authService.spaName === 'work' || this.authService.spaName === 'mgmt') {
            const areaKey = this.authService.getAreaKey();
            const area = this.filteredAreas.find(a => a.Name === areaKey);
            return area ? (area.Alias ?? area.Name) : null;
        }

        if (this.authService.spaName === 'conf') {
            return 'Config';
        }

        return null;
    }

    @HostBinding('user')
    public get hostUser(): {
        initials: string;
        name: string;
        subline1: string;
        subline2: string;
        image: string;
        disabled: boolean;
    } {
        return this.auroraUser;
    }

    @HostBinding('areas')
    public get hostAreas(): Area[] {
        if (this.authService.spaName === 'work' || this.authService.spaName === 'mgmt') {
            return this.filteredAreas;
        }

        return AuroraHeaderDirective.NoAreas;
    }

    @HostBinding('userLinks')
    public get hostUserLinks(): string[] {
        return this.auroraUserLinks;
    }

    @HostBinding('info')
    public get hostInfo(): { label: string; value: string }[] {
        return this.auroraInfo;
    }

    @HostBinding('attr.sync-status')
    public get hostSyncStatus(): string {
        return this.syncStatus?.toLowerCase?.() ?? 'connected';
    }

    constructor(
        private hostElementRef: ElementRef<HTMLElement>,
        private router: Router,
        private userService: UserService,
        private authService: AuthService,
        private featureFacetService: FeatureFacetService,
        private syncHubStatusService: SyncHubStatusService,
        private logger: LoggingService,
        private modalService: NgbModal,
        private tenantContext: TenantContext,
        private viewContainerRef: ViewContainerRef,
    ) {
    }

    ngOnInit() {
        this.setAuroraUserLinks();
        this.getUserAndBadge();
        this.setAuroraInfo();
        this.scheduleSyncStatusValueStyleUpdate();
        this.scheduleTenantSwitchStyleUpdate();

        this.syncHubStatusService.status
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((status: string) => {
                this.syncStatus = <SyncStatus>status;
                this.setAuroraInfo();
                this.scheduleSyncStatusValueStyleUpdate();
            });

        if (this.view !== 'Admin') {
            combineLatest([
                this.authService.authContext$,
                this.tenantContext.getStore(new Area()).values
            ])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(([authContext, areas]) => {
                    if (!authContext || !areas?.length) {
                        return;
                    }

                    const areasUserHasAccessTo = this.authService.getAvailableAreasForCurrentTenant();
                    this.areas = _.sortBy(areas, a => a.Order);
                    this.filteredAreas = [];

                    _.each(areasUserHasAccessTo, area => {
                        const foundArea = _.find(this.areas, a => a.Name === area);
                        if (foundArea) {
                            this.filteredAreas.push(foundArea);
                        }
                    });

                    this.filteredAreas = _.sortBy(this.filteredAreas, a => a.Order);
                    this.scheduleTenantSwitchStyleUpdate();
                });

            this.featureFacetService.getAllFacets()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(facets => {
                    this.allFacets = facets;
                });
        }
    }

    private getUserAndBadge(): void {
        if (this.view === 'Admin') {
            this.userService.AdminUser$
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(adminUser => {
                    if (!adminUser) {
                        return;
                    }

                    this.adminUser = adminUser;
                    this.badge = {
                        Type: 'User',
                        Id: this.adminUser.Id,
                        Name: this.adminUser.DisplayName,
                        Initials: this.adminUser.DisplayName.charAt(0).toUpperCase(),
                        Title: '',
                        Email: this.adminUser.Logon,
                        Disabled: !this.adminUser.IsActive,
                        IsCurrentUser: this.adminUser.Id == this.authService.getIdentityKey()
                    };
                    this.buildAuroraUser();
                });
        } else {
            this.userService.User$
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(tenantUser => {
                    if (!tenantUser) {
                        return;
                    }

                    this.user = tenantUser;
                    this.badge = {
                        Type: 'User',
                        Id: this.user.Id,
                        Name: this.user.DisplayName,
                        Initials: this.user.Initials,
                        Title: this.user.Title,
                        Email: this.user.Email,
                        Disabled: !this.user.Active,
                        IsCurrentUser: this.user.Id == this.authService.getIdentityKey()
                    };
                    this.buildAuroraUser();
                });
        }
    }

    private buildAuroraUser(): void {
        if (!this.badge) {
            return;
        }

        this.auroraUser = {
            initials: this.badge.Initials ?? 'U',
            name: this.badge.Name ?? 'User',
            subline1: this.badge.Title ?? '',
            subline2: this.hostTenant ?? '',
            image: '',
            disabled: this.badge.Disabled ?? false
        };
    }

    private setAuroraUserLinks(): void {
        if (this.view === 'Admin') {
            this.auroraUserLinks = ['Log Off'];
            return;
        }

        this.auroraUserLinks = [
            'Profile',
            'Notification Settings',
            'Reset Password',
            'Log Off'
        ];
    }

    private setAuroraInfo(): void {
        if (this.view === 'Admin') {
            this.auroraInfo = AuroraHeaderDirective.NoInfo;
            return;
        }

        this.auroraInfo = [{
            label: 'Live Updates Status:',
            value: this.getSyncStatusDisplayValue(this.syncStatus)
        }];
    }

    private scheduleSyncStatusValueStyleUpdate(): void {
        requestAnimationFrame(() => this.applySyncStatusValueStyle());
    }

    private scheduleTenantSwitchStyleUpdate(): void {
        requestAnimationFrame(() => this.applyTenantSwitchStyle());
    }

    private ensureSyncStatusStyles(shadowRoot: ShadowRoot): void {
        if (shadowRoot.getElementById(AuroraHeaderDirective.SyncStatusStyleElementId)) {
            return;
        }

        const styleElement = document.createElement('style');
        styleElement.id = AuroraHeaderDirective.SyncStatusStyleElementId;
        styleElement.textContent = `
            .sm-sync-status-value {
                font-weight: 700;
            }

            .sm-sync-status-value.connected {
                color: var(--aurora-semantic-foreground-feedback-success-bright-default);
            }

            .sm-sync-status-value.reconnecting {
                color: var(--aurora-semantic-foreground-feedback-warning-bright-default);
            }

            .sm-sync-status-value.disconnected {
                color: var(--aurora-semantic-foreground-feedback-error-bright-default);
            }
        `;

        shadowRoot.appendChild(styleElement);
    }

    private ensureTenantSwitchStyles(shadowRoot: ShadowRoot): void {
        if (shadowRoot.getElementById(AuroraHeaderDirective.TenantSwitchStyleElementId)) {
            return;
        }

        const styleElement = document.createElement('style');
        styleElement.id = AuroraHeaderDirective.TenantSwitchStyleElementId;
        styleElement.textContent = `
            .sm-tenant-click-target {
                display: inline-flex;
                align-items: center;
                gap: 0.25rem;
                cursor: pointer;
            }

            .sm-tenant-click-target .sm-tenant-switch-icon {
                pointer-events: none;
            }
        `;

        shadowRoot.appendChild(styleElement);
    }

    private applyTenantSwitchStyle(): void {
        const shadowRoot = this.hostElementRef.nativeElement.shadowRoot;
        const companyNameElement = shadowRoot?.querySelector<HTMLElement>('[part="company-name"]');

        if (!shadowRoot || !companyNameElement) {
            return;
        }

        this.ensureTenantSwitchStyles(shadowRoot);

        const existingClickable = companyNameElement.querySelector<HTMLElement>('.sm-tenant-click-target');
        const canSwitch = this.canSwitchTenants();

        if (canSwitch) {
            if (!existingClickable) {
                const tenantText = companyNameElement.textContent?.trim() ?? '';
                if (!tenantText) {
                    return;
                }

                companyNameElement.textContent = '';
                const clickable = document.createElement('span');
                clickable.className = 'sm-tenant-click-target';

                const tenantTextNode = document.createElement('span');
                tenantTextNode.textContent = tenantText;
                clickable.appendChild(tenantTextNode);

                const switchIcon = document.createElement('aurora-icon');
                switchIcon.className = 'sm-tenant-switch-icon';
                switchIcon.setAttribute('icon', 'swap');
                clickable.appendChild(switchIcon);

                companyNameElement.appendChild(clickable);
            }
            return;
        }

        if (existingClickable) {
            companyNameElement.textContent = existingClickable.textContent ?? '';
        }
    }

    private canSwitchTenants(): boolean {
        if (!(this.authService.spaName === 'work' || this.authService.spaName === 'mgmt' || this.authService.spaName === 'conf')) {
            return false;
        }

        return (this.authService.getAvailableTenants()?.length ?? 0) > 1;
    }

    private applySyncStatusValueStyle(): void {
        if (this.view === 'Admin') {
            return;
        }

        const shadowRoot = this.hostElementRef.nativeElement.shadowRoot;
        const appInfoLabelElement = shadowRoot?.querySelector<HTMLElement>('[part="app-info-label"]');

        if (!shadowRoot || !appInfoLabelElement) {
            return;
        }

        this.ensureSyncStatusStyles(shadowRoot);

        const statusValue = this.getSyncStatusDisplayValue(this.syncStatus);
        const statusClass = this.syncStatus?.toLowerCase?.() ?? 'connected';

        appInfoLabelElement.replaceChildren();
        appInfoLabelElement.append(document.createTextNode('Live Updates Status: '));

        const statusValueElement = document.createElement('span');
        statusValueElement.classList.add('sm-sync-status-value', statusClass);
        statusValueElement.textContent = statusValue;

        appInfoLabelElement.append(statusValueElement);
    }

    private getSyncStatusDisplayValue(status: SyncStatus): string {
        switch (status) {
            case SyncStatus.Connected:
                return 'Connected';
            case SyncStatus.Reconnecting:
                return 'Reconnecting';
            case SyncStatus.Disconnected:
            default:
                return 'Disconnected';
        }
    }

    @HostListener('aurora-area-click')
    public onAuroraAreaClick(): void {
        const modalRef = this.modalService.open(SelectAreaModalComponent, {
            ariaLabelledBy: 'modal-basic-title',
            backdrop: 'static',
            windowClass: 'no-bg'
        });
        const modalComponent = modalRef.componentInstance as SelectAreaModalComponent;

        modalComponent.selectedAreaAliasOrName = this.hostArea;
        modalComponent.areas = this.filteredAreas;

        modalRef.result.then(() => { }).catch(() => { });
    }

    @HostListener('aurora-user-link-click', ['$event'])
    public onAuroraUserLinkClick(event: Event): void {
        const link = (event as CustomEvent)?.detail?.link;

        switch (link) {
            case 'Profile':
                this.userSettings();
                break;
            case 'Notification Settings':
                this.notificationSettings();
                break;
            case 'Reset Password':
                this.resetPassword();
                break;
            case 'Log Off':
                this.logger.logEvent('User Action - Header: Log Off');
                this.authService.logout();
                break;
            default:
                break;
        }
    }

    @HostListener('click', ['$event'])
    public onHeaderClick(event: Event): void {
        const clickPath = (event as MouseEvent).composedPath?.() ?? [];

        // aurora-header's brand mark is a hard href="/", which reloads into the login flow.
        if (clickPath.some(node => (node as Element)?.part?.contains('brand-mark'))) {
            event.preventDefault();
            this.router.navigate([this.view]);
            return;
        }

        if (!this.canSwitchTenants()) {
            return;
        }

        const clickedCompanyName = clickPath.some(node =>
            (node as HTMLElement)?.classList?.contains('sm-tenant-click-target'));

        if (!clickedCompanyName) {
            return;
        }

        const dialogOptions: SimpleDialogOptions = {
            message: 'Are you sure you want to switch tenants?',
            title: 'Switch Tenants',
            buttons: {
                leftButton: {
                    label: 'Confirm',
                    action: (activeModal: NgbActiveModal) => {
                        activeModal.close('cancel');
                    }
                },
                rightButton: {
                    label: 'Cancel',
                    action: (activeModal: NgbActiveModal) => {
                        activeModal.dismiss('cancel button');
                    }
                }
            }
        };

        const modalRef = this.modalService.open(SmSimpleDialog, { backdrop: 'static', centered: true });
        modalRef.componentInstance.options = dialogOptions;
        modalRef.result.then(() => {
            this.router.navigateByUrl('');
        }, () => { });
    }

    private userSettings() {
        if (!this.user) {
            return;
        }

        const modalRef = this.modalService.open(SmUserInfoComponent, {
            backdrop: 'static',
            centered: true
        });
        modalRef.componentInstance.user = this.user;

        return modalRef;
    }

    // aurora-modal is a native <dialog> with its own backdrop, so it is rendered into the
    // directive's view container rather than opened through NgbModal.
    private notificationSettings() {
        const settingsRef: ComponentRef<SmNotificationSettingsComponent> =
            this.viewContainerRef.createComponent(SmNotificationSettingsComponent);
        const close = () => settingsRef.destroy();

        settingsRef.instance.save.subscribe(close);
        settingsRef.instance.cancel.subscribe(close);

        return settingsRef;
    }

    private resetPassword() {
        if (!this.user?.Email) {
            this.passwordResetFinished(false);
            return;
        }

        const dialogOptions: SimpleDialogOptions = {
            message: 'Do you want to send an email to reset your password?',
            title: 'Reset Password',
            buttons: {
                leftButton: {
                    label: 'Send Reset Password Email',
                    action: (activeModal: NgbActiveModal) => {
                        this.resetUsersPassword(activeModal);
                    }
                },
                rightButton: {
                    label: 'Cancel',
                    action: (activeModal: NgbActiveModal) => {
                        activeModal.dismiss('cancel');
                    }
                }
            }
        };

        const dialog = this.modalService.open(SmSimpleDialog, {
            backdrop: 'static',
            centered: true
        });
        dialog.componentInstance.options = dialogOptions;
    }

    private resetUsersPassword(passwordModal: NgbActiveModal) {
        this.authService.recoverPassword(this.user.Email).subscribe({
            next: () => {
                passwordModal.close('complete');
                this.passwordResetFinished(true);
            },
            error: () => {
                this.passwordResetFinished(false);
            }
        });
    }

    private passwordResetFinished(wasSuccessful: boolean) {
        const dialogOptions: SimpleDialogOptions = {
            message: wasSuccessful ? 'Password reset email sent' : 'An error occurred',
            title: wasSuccessful ? 'Success' : 'Error',
            buttons: {
                leftButton: {
                    label: 'Ok',
                    action: (activeModal: NgbActiveModal) => {
                        activeModal.close('complete');
                    }
                }
            }
        };

        const dialog = this.modalService.open(SmSimpleDialog, {
            backdrop: 'static',
            centered: true
        });
        dialog.componentInstance.options = dialogOptions;
    }
}
